import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import { useGetInformationTypeQuery } from '@/api/common';
import SetSubTitleDialog from './components/SetSubTitleDialog';
import { InformationType } from '@/api/enum/common.enum';
import SetOrderDialog from './components/SetOrderDialog';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddPageDialog from './components/AddPageDialog';
import {
	FaqPageFilter,
	InformationQaOverviewDTO,
} from '@/api/DTO/information/faq.interface';
import { useGetFaqListQuery } from '@/api/information/faqApiSlice';
import { FormType } from '@/api/enum/formType.enum';
import FaqListSummaryFilterSection from './components/FaqListSummaryFilterSection';

export interface faqListDataGrid {
	updatedAt: string;
	createdAt: string;
}

export default function FaqListSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);

	const defaultPropertyFilter: FaqPageFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<FaqPageFilter> = defaultPropertyFilter;
	const methods = useForm<FaqPageFilter>({
		defaultValues,
	});
	const transformedSearchParams: FaqPageFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetFaqListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});
	const { data: informationTypeData } = useGetInformationTypeQuery(
		InformationType.FAQ,
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const transformResponse = useCallback(
		(response?: InformationQaOverviewDTO[]) => {
			if (response == null) return;
			const newResponse: faqListDataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as FaqPageFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('faq.cn_title'),
			flex: 21,
		},
		{
			field: 'titleEn',
			headerName: t('faq.en_title'),
			flex: 15,
		},
		{
			field: 'qaCount',
			headerName: t('faq.qaCount'),
			flex: 21,
		},
		{
			field: 'updatedAt',
			headerName: t('faq.lastUpdateTime'),
			flex: 21,
		},
		{
			field: 'createdAt',
			headerName: t('faq.createTime'),
			flex: 21,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/information-manage/faq-list/sub-faq-list/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
		{
			field: 'delete',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<AddPageDialog type={FormType.edit} row={params.row} />
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.faqList')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetOrderDialog />
					<AddPageDialog type={FormType.create} />
				</Stack>
			</Box>
			<Box display='flex' alignItems={'center'} marginTop={'12px'}>
				<Typography variant='body1'>
					{t('faq.subTitle')}：
					{informationTypeData?.data.subtitleZh
						? informationTypeData?.data.subtitleZh
						: informationTypeData?.data.subtitleEn
						? informationTypeData?.data.subtitleEn
						: '-'}
				</Typography>
				<SetSubTitleDialog
					informationTypeData={informationTypeData}
				></SetSubTitleDialog>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<FaqListSummaryFilterSection></FaqListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
